<template>
  <span class="address-text" :aria-busy="loading">
    {{ displayAddress }}
    <small v-if="errorMessage" class="address-error" role="alert">
      {{ errorMessage }}
      <button v-if="retryable" type="button" @click="load">{{ $t('sensitiveView.retry') }}</button>
    </small>
  </span>
</template>

<script>
import { sensitiveViewPlaintext } from '@/api/user';

const ADDRESS_FIELDS = [
  'receiveStreet', 'receiveHouseNumber', 'receiveArea', 'receiveCity',
  'receiveState', 'receiveZipcode', 'receiveAddress'
];
const isHidden = value => typeof value === 'string' &&
  (value.trim().startsWith('{$') || /^\*{4,}$/.test(value.trim()));

export default {
  name: 'CurrentAddress',
  props: { address: { type: Object, default: () => ({}) }},
  data() {
    return { displayFields: {}, errorMessage: '', retryable: false, loading: false, requestVersion: 0 };
  },
  computed: {
    identity() {
      return `${this.$store.getters.userId || ''}:${this.$store.getters.token || ''}`;
    },
    canView() {
      return (this.$store.getters.button_permissions || []).includes('field:customer-sensitive-info');
    },
    displayAddress() {
      const fields = this.displayFields;
      const zip = fields.receiveZipcode;
      const cleanZip = zip && zip.toString().replace(/\D/g, '');
      const formattedZip = cleanZip && cleanZip.length === 8
        ? `${cleanZip.slice(0, 5)}-${cleanZip.slice(5)}` : zip;
      return [fields.receiveStreet, fields.receiveHouseNumber, fields.receiveArea,
        fields.receiveCity, fields.receiveState, formattedZip, fields.receiveAddress]
        .filter(Boolean).join(', ') || '--';
    }
  },
  watch: {
    address: { handler: 'load', deep: true },
    identity: 'load',
    canView: 'load'
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.requestVersion++;
    this.displayFields = {};
  },
  methods: {
    async load() {
      const version = ++this.requestVersion;
      const identity = this.identity;
      const source = Object.fromEntries(ADDRESS_FIELDS.map(field => [field, this.address[field] || '']));
      this.displayFields = {};
      this.errorMessage = '';
      this.retryable = false;
      this.loading = false;
      if (!Object.values(source).some(Boolean)) return;
      if (!this.canView) {
        this.errorMessage = this.$t('sensitiveView.forbidden');
        return;
      }
      if (Object.values(source).some(value => typeof value === 'string' && /^\*{4,}$/.test(value.trim()))) {
        this.errorMessage = this.$t('sensitiveView.refresh');
        return;
      }
      if (!Object.values(source).some(isHidden)) {
        this.displayFields = source;
        return;
      }
      this.loading = true;
      try {
        const response = await sensitiveViewPlaintext({ data: source });
        if (version !== this.requestVersion || identity !== this.identity || !this.canView) return;
        if (!response || response.code !== 200 || !response.data ||
          ADDRESS_FIELDS.some(field => isHidden(response.data[field]))) {
          throw new Error('Invalid address response');
        }
        this.displayFields = response.data;
      } catch (error) {
        if (version === this.requestVersion) {
          this.errorMessage = this.$t(error.sensitiveMessageKey || 'sensitiveView.failed');
          this.retryable = true;
        }
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.address-error { display: block; color: #d93025; }
.address-error button { margin-left: 8px; border: 0; background: none; color: #1267a3; cursor: pointer; }
</style>
